<template>
  <div class="flex flex-col gap-4 md:gap-3">
    <div>
      <RichText v-if="emailHeading" :field="emailHeading" class="text-lg-bold text-racq-dark-grey" />
      <RichText v-if="emailSubheading" :field="emailSubheading" class="text-base text-racq-med-grey mt-1" />
    </div>
    <div
      :class="
        plainStyle
          ? 'px-5 py-8 bg-racq-anti-flash-white rounded'
          : 'px-5 md:px-8 pt-5 md:pt-6 pb-8 bg-racq-anti-flash-white border-2 rounded border-solid border-racq-pale-blue-border shadow-lg'
      "
    >
      <FormKit v-model="formData" type="form" novalidate :disabled="bindings.disabled" @submit="emailSendClick">
        <div :class="['flex flex-col gap-4 md:gap-5', { 'md:px-16': plainStyle }]">
          <div class="flex gap-5 flex-col md:flex-row">
            <RACQFormKit
              :bindings="{
                ...bindings,
                helpClass: 'mt-1',
                innerClass: '',
                outerClass: plainStyle ? 'w-full' : 'md:racq-w-xl w-full',
              }"
            />
            <div>
              <FormKit
                type="submit"
                :loading="isLoading"
                :disabled="plainStyle ? undefined : sendButtonDisabled"
                :inputClass="
                  plainStyle
                    ? 'btn btn-md btn-secondary w-full md:w-auto md:!min-w-[130px]'
                    : 'btn btn-md btn-secondary md:mt-7 w-full md:w-auto md:!min-w-[130px]'
                "
                :label="emailButtonText.value"
              />
            </div>
          </div>
          <Alert v-if="alert" :alert="alert" />
        </div>
      </FormKit>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import RACQFormKit from '../RACQFormKit/RACQFormKit.vue';
import Alert from '../Alert/Alert.vue';
import { IAlert } from '@/interfaces/forms/alert-interface';
import { AlertTypes } from '@/enums/alert-types-enum';
import { computed, ref, watch } from 'vue';

// Define props and emitters
// ------------------------------------------------------------- //
const props = defineProps<{
  bindings: any;
  emailHeading?: { value: string };
  emailSubheading?: { value: string };
  emailButtonText: { value: string };
  successText: { value: string };
  errorText: { value: string };
  infoText?: { value: string };
  isLoading: boolean;
  showSuccess: boolean;
  showError: boolean;
  showInfo?: boolean;
  sendButtonDisabled?: boolean;
  // When true, renders the borderless, centered layout used on the DS-4965 payment confirmation page.
  plainStyle?: boolean;
}>();

const emailAddress = defineModel<string>();

const emit = defineEmits(['sendEmailFired']);

watch(
  () => emailAddress.value,
  (newVal) => {
    formData.value[props.bindings.name] = newVal ?? '';
  }
);

// Define Component state
// ------------------------------------------------------------- //
const formData = ref<Record<string, string>>({});

const sendButtonDisabled = computed(() => {
  return (
    !formData.value?.[props.bindings.name] || props.isLoading || props.bindings.disabled || props.sendButtonDisabled
  );
});

const alert = computed<IAlert | null>(() => {
  if (props.showSuccess) {
    return {
      showIcon: true,
      alertType: AlertTypes.success,
      text: props.successText.value,
    };
  } else if (props.showInfo) {
    return {
      showIcon: true,
      alertType: AlertTypes.info,
      text: props.infoText.value,
    };
  } else if (props.showError) {
    return {
      showIcon: true,
      alertType: AlertTypes.danger,
      text: props.errorText.value,
    };
  }
  return null;
});

// Define Functions
// ------------------------------------------------------------- //

const emailSendClick = (values: Record<string, string>) => {
  console.debug(formData.value);
  !sendButtonDisabled.value && emit('sendEmailFired', values);
};
</script>
